import {
  AfterViewInit,
  Directive,
  ElementRef,
  Host,
  Input,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { BannerSlide } from '@core/interfaces/banners';
import { Carousel } from 'primeng/carousel';
import { Subscription } from 'rxjs';

const DEFAULT_WATCH_TIME_MS = 6000;

@Directive({
  selector: 'p-carousel[appBannerAutoplay]',
})
export class BannerAutoplayDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input('appBannerAutoplay') banners: Pick<BannerSlide, 'watchTime'>[] = [];

  private timer?: ReturnType<typeof setTimeout>;
  private hovered = false;
  private pageSubscription?: Subscription;
  private hoverArea?: HTMLElement | null;
  private readonly pause = () => {
    this.hovered = true;
    this.clear();
  };
  private readonly resume = () => {
    this.hovered = false;
    this.schedule();
  };

  constructor(
    @Host() private readonly carousel: Carousel,
    private readonly element: ElementRef<HTMLElement>
  ) {}

  ngAfterViewInit(): void {
    this.pageSubscription = this.carousel.onPage.subscribe(() => this.schedule());
    this.hoverArea = this.element.nativeElement.parentElement;
    this.hoverArea?.addEventListener('mouseenter', this.pause);
    this.hoverArea?.addEventListener('mouseleave', this.resume);
    this.schedule();
  }

  ngOnChanges(): void {
    this.schedule();
  }

  ngOnDestroy(): void {
    this.clear();
    this.pageSubscription?.unsubscribe();
    this.hoverArea?.removeEventListener('mouseenter', this.pause);
    this.hoverArea?.removeEventListener('mouseleave', this.resume);
  }

  private schedule(): void {
    this.clear();
    const total = this.banners?.length ?? 0;
    if (this.hovered || total < 2) {
      return;
    }
    const current = this.carousel.page ?? 0;
    this.timer = setTimeout(() => {
      this.carousel.navForward(null, current >= total - 1 ? 0 : current + 1);
    }, this.watchTimeOf(current));
  }

  private watchTimeOf(index: number): number {
    const seconds = Number(this.banners[index]?.watchTime);
    return seconds > 0 ? seconds * 1000 : DEFAULT_WATCH_TIME_MS;
  }

  private clear(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }
}
